import { MembershipType } from '../types';
import { getFeeCents, membershipTypeToFeeKey } from '@shared/fees';
import { clubYear, isJuniorForYear } from '@shared/membership';

export function getMembershipPrice(type: MembershipType): number {
  const key = membershipTypeToFeeKey(type);
  return key ? (getFeeCents(key) ?? 0) / 100 : 0;
}

export function getMembershipLabel(type: MembershipType | string): string {
  const LABELS: Record<string, string> = {
    [MembershipType.FAMILY]:         'Family Membership',
    [MembershipType.ADULT]:          'Adult Individual Member',
    [MembershipType.JUNIOR]:         'Junior Individual Member',
    [MembershipType.SPONSOR_GOLD]:   'Sponsor — Gold',
    [MembershipType.SPONSOR_SILVER]: 'Sponsor — Silver',
    [MembershipType.SPONSOR_BRONZE]: 'Sponsor — Bronze',
    SINGLE:                          'Adult Individual Member',
  };
  return LABELS[type] || String(type);
}

export function getMembershipPriceByString(type: string): number {
  const key = membershipTypeToFeeKey(type);
  return key ? (getFeeCents(key) ?? 0) / 100 : 0;
}

/** Exact age today, for display only. Not the junior/adult decision (#127, D7) — use isUnderAge for that. */
export function calculateAge(dateOfBirth: string): number {
  const today = new Date();
  const birth = new Date(dateOfBirth);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * The junior/adult decision (#127, D7): true when `dateOfBirth` is a junior
 * (16 or under on 1 January, not yet 17) for the club year of `referenceDate`.
 * Delegates to `@shared/membership` so the frontend agrees with the server.
 * `referenceDate` is kept for compatibility with existing callers; only its
 * club year is used.
 */
export function isUnderAge(dateOfBirth: string, referenceDate: Date): boolean {
  return isJuniorForYear(dateOfBirth, clubYear(referenceDate));
}

/** @deprecated Use isUnderAge(dob, new Date()) instead. */
export function isUnder16(dateOfBirth: string): boolean {
  return isUnderAge(dateOfBirth, new Date());
}


export interface RenewalStatusDisplay {
  colour: string;
  bgColour: string;
  text: string;
  showRenewButton: boolean;
}

export function getRenewalStatusDisplay(
  renewalStatus: string,
  membershipYear?: number
): RenewalStatusDisplay {
  switch (renewalStatus) {
    case 'CURRENT':
      return {
        colour: 'text-green-800',
        bgColour: 'bg-green-100',
        text: `Current until 31 Dec ${membershipYear ?? clubYear()}`,
        showRenewButton: false,
      };
    case 'PENDING_RENEWAL':
      return {
        colour: 'text-amber-800',
        bgColour: 'bg-amber-100',
        text: 'Renewal due — renew by 28 February',
        showRenewButton: true,
      };
    case 'EXPIRED':
      return {
        colour: 'text-red-800',
        bgColour: 'bg-red-100',
        text: 'Membership expired',
        showRenewButton: true,
      };
    case 'LAPSED':
      return {
        colour: 'text-red-800',
        bgColour: 'bg-red-100',
        text: 'Membership lapsed — contact the club to reinstate',
        showRenewButton: false,
      };
    default:
      return {
        colour: 'text-gray-800',
        bgColour: 'bg-gray-100',
        text: 'Unknown status',
        showRenewButton: false,
      };
  }
}

/**
 * Local member state after a successful online renewal (#108). Mirrors
 * process-payment's buildMemberUpdate: CURRENT for the club (Sydney) year,
 * expiring 31 Dec, active. 'RENEWED' was used before, which is not a
 * renewalStatus value, so the dashboard showed "Unknown status".
 */
export function applyOnlineRenewal<T extends Record<string, any>>(member: T, now: Date = new Date()): T {
  const year = clubYear(now);
  return {
    ...member,
    renewalStatus: 'CURRENT',
    membershipYear: year,
    membershipExpiryDate: `${year}-12-31`,
    isActive: true,
    paymentReceived: true,
  };
}
